import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DateTime } from "luxon";
import "./TripAI.css";
import "./VoiceTripIntentFlow.css";
import { API_BASE_URL } from "../../Common/Common";
import { handOverDays } from "../../Common/DestinationValidation";

const WHISPER_URL = `${API_BASE_URL}/whisper_send_data.php`;
const PARSE_URL = `${API_BASE_URL}/parse_trip_intent.php`;

// "Tell us about your trip" — voice entry point for Build Trip with AI.
// Records via the same browser MediaRecorder + whisper_send_data.php pattern
// CommandPalette.js already uses, but always shows the transcript back to
// the user as editable text before parsing it (a short search query doesn't
// need this; a full spoken trip description that then overwrites Destinations
// + Preferences does — a misheard city or date should be fixable before it
// silently replaces what's there). See parse_trip_intent.php for the
// structured-extraction endpoint this calls next.
//
// Props: onApply(destinations, aiPreferences) — destinations in the same
// {id, city, country, dateStart, dateEnd} shape DestinationsScreen.js/
// AIDestinationsFlow.js use (dateStart/dateEnd as Luxon DateTimes);
// aiPreferences in the same shape PreferencesScreen.js writes to
// window.guide_obj.aiPreferences. onCancel().
const VoiceTripIntentFlow = ({ onApply, onCancel, dayStartHour = 9, dayEndHour = 20 }) => {
  const [phase, setPhase] = useState("record"); // "record" | "review" | "parsing"
  const [micState, setMicState] = useState("idle"); // idle | recording | transcribing | error
  const [transcript, setTranscript] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const micStreamRef = useRef(null);
  const elapsedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      stopMicStream();
      clearInterval(elapsedTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopMicStream = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setMicState("error");
      return;
    }
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
        clearInterval(elapsedTimerRef.current);
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        transcribe(blob);
      };
      recorder.start();
      setMicState("recording");
      setElapsed(0);
      elapsedTimerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch (e) {
      setMicState("error");
    }
  };

  const transcribe = (blob) => {
    setMicState("transcribing");
    const formData = new FormData();
    formData.append("audio_file", blob, "recording.webm");
    fetch(WHISPER_URL, { method: "POST", body: formData })
      .then((r) => r.json())
      .then((res) => {
        setMicState("idle");
        if (res.text && res.text.trim()) {
          setTranscript(res.text.trim());
          setPhase("review");
        } else {
          setError("Couldn't make out what you said — try again.");
          setMicState("error");
        }
      })
      .catch(() => {
        setMicState("error");
        setError("Couldn't reach the transcription service — try again.");
      });
  };

  const toggleMic = () => {
    if (micState === "recording") {
      mediaRecorderRef.current && mediaRecorderRef.current.stop();
    } else if (micState === "idle" || micState === "error") {
      startRecording();
    }
  };

  const fmtElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const parseTranscript = () => {
    const text = transcript.trim();
    if (!text) {
      setError("Enter or re-record a description of the trip first.");
      return;
    }
    setError("");
    setPhase("parsing");
    fetch(PARSE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (!res.found) {
          setError(res.reason || "Couldn't understand a trip in that description — try rewording it.");
          setPhase("review");
          return;
        }
        // A voice description often won't mention explicit dates ("I want to
        // go to Paris and Rome") — every other destination-producing flow in
        // the app (DestinationsScreen's own "Add City") guarantees real
        // Luxon dates on every destination, defaulting to tomorrow (or the
        // day after the previous destination's end, when chaining), and
        // TripAI.js's _totalDays() (among other consumers) assumes that
        // invariant unconditionally — a null dateStart crashes there. Default
        // the same way DestinationsScreen does instead of leaving it null;
        // the user can still correct the guessed dates via Edit Destinations
        // afterward. Same fix as mobile's VoiceTripIntentScreen.js. Found
        // 2026-09-26.
        let cursor = null;
        const destinations = (res.destinations || []).map((d, i) => {
          const parsedStart = d.dateStart ? DateTime.fromISO(d.dateStart) : null;
          const parsedEnd = d.dateEnd ? DateTime.fromISO(d.dateEnd) : null;
          const fallbackStart = cursor
            ? cursor.plus({ days: 1 }).startOf("day")
            : DateTime.fromISO(DateTime.now().toISO({ zone: "utc" }), { zone: "utc" }).startOf("day").plus({ days: 1 });
          const start = (parsedStart || fallbackStart).set({ hour: dayStartHour, minute: 0 });
          // "For 1 week"/"10 days" carries real length info even with no
          // start date — parse_trip_intent.php surfaces that as
          // durationDays precisely so it isn't silently discarded down to a
          // 1-day guess the way a bare fallback would. -1 since a 7-day trip
          // starting on day 1 ends on day 7, not day 8.
          const end = parsedEnd
            || (d.durationDays > 0 ? start.plus({ days: d.durationDays - 1 }) : null)
            || parsedStart
            || fallbackStart;
          const endWithHour = end.set({ hour: dayEndHour, minute: 0 });
          cursor = endWithHour;
          return {
            id: Date.now() + i,
            city: d.city,
            country: d.country || "",
            dateStart: start,
            dateEnd: endWithHour,
          };
        });
        onApply(handOverDays(destinations), res.aiPreferences || {});
      })
      .catch(() => {
        setError("Something went wrong parsing that — try again.");
        setPhase("review");
      });
  };

  return (
    <div className="tai-paste-overlay" onClick={onCancel}>
      <div className="tai-paste-modal vtif-modal" onClick={(e) => e.stopPropagation()}>
        <p className="tai-paste-title">Tell us about your trip</p>
        {phase === "record" && (
          <>
            <p className="tai-paste-hint">
              Speak naturally — e.g. "A 4-day romantic food trip to Tokyo in October on a mid-range budget."
            </p>
            {error !== "" && <p className="tai-paste-error">{error}</p>}
            <div className="vtif-record-area">
              <button
                className={`vtif-mic-btn${micState === "recording" ? " vtif-mic-btn-recording" : ""}`}
                onClick={toggleMic}
                disabled={micState === "transcribing"}
                aria-label={micState === "recording" ? "Stop recording" : "Start recording"}
              >
                <FontAwesomeIcon
                  icon={micState === "transcribing" ? solid("spinner") : solid("microphone")}
                  spin={micState === "transcribing"}
                />
              </button>
              <p className="vtif-mic-caption">
                {micState === "recording"
                  ? `Recording ${fmtElapsed(elapsed)} — tap to stop`
                  : micState === "transcribing"
                  ? "Transcribing…"
                  : "Tap the microphone to start"}
              </p>
            </div>
            <div className="tai-paste-actions">
              <button className="tai-cancel-btn" onClick={onCancel}>Cancel</button>
              <button
                className="tai-cancel-btn"
                onClick={() => setPhase("review")}
              >
                Type instead
              </button>
            </div>
          </>
        )}
        {(phase === "review" || phase === "parsing") && (
          <>
            <p className="tai-paste-hint">Review what we heard — fix anything before continuing.</p>
            {error !== "" && <p className="tai-paste-error">{error}</p>}
            <textarea
              className="tai-paste-input vtif-transcript"
              rows={4}
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              disabled={phase === "parsing"}
              placeholder="e.g. A 4-day romantic food trip to Tokyo in October on a mid-range budget, but make sure we visit Akihabara."
              autoFocus
            />
            <div className="tai-paste-actions">
              <button className="tai-cancel-btn" onClick={onCancel} disabled={phase === "parsing"}>Cancel</button>
              <button
                className="tai-cancel-btn"
                onClick={() => { setPhase("record"); setError(""); }}
                disabled={phase === "parsing"}
              >
                <FontAwesomeIcon icon={solid("microphone")} />
                Re-record
              </button>
              <button className="tai-primary-btn" onClick={parseTranscript} disabled={phase === "parsing"}>
                {phase === "parsing" ? "Understanding…" : "Continue"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceTripIntentFlow;
